import React, { useEffect, useState } from "react";
import { ShieldAlert, ShieldCheck, ChevronDown, ChevronUp, AlertTriangle, FileDown, Lock } from "lucide-react";
import { hasSellerTools, openSubscribeGate, useSubscription } from "../../lib/subscription";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

/** Derive NHTSA's base model name from whatever vehicle shape we get —
    live Tesla profiles carry `.model`, catalog entries only `.short`/`.name`. */
const baseModel = (vehicle) => {
  const s = `${vehicle?.model || ""} ${vehicle?.short || ""} ${vehicle?.name || ""}`.toLowerCase();
  if (vehicle?.brand === "ford") return vehicle?.model || null;
  if (s.includes("model 3")) return "Model 3";
  if (s.includes("model y")) return "Model Y";
  if (s.includes("model s")) return "Model S";
  if (s.includes("model x")) return "Model X";
  if (s.includes("cybertruck")) return "Cybertruck";
  if (s.includes("roadster")) return "Roadster";
  return vehicle?.model || null;
};

const RecallItem = ({ r }) => {
  const [open, setOpen] = useState(false);
  return (
    <button
      type="button"
      onClick={() => setOpen((o) => !o)}
      data-testid={`recall-item-${r.campaign}`}
      className="w-full text-left rounded-xl px-3 py-2.5 transition-colors hover:bg-white/[0.04]"
      style={{ border: "1px solid rgba(255,255,255,0.08)", background: "rgba(255,255,255,0.02)" }}
    >
      <div className="flex items-center gap-2">
        <div className="flex-1 min-w-0">
          <div className="text-[12px] font-semibold text-white leading-tight truncate">
            {(r.component || "Recall").split(":")[0]}
          </div>
          <div className="font-mono text-[9px] uppercase tracking-[0.12em] text-zinc-500 mt-0.5 flex items-center flex-wrap gap-x-2 gap-y-0.5">
            <span>{r.campaign} · {r.report_date}</span>
            {r.ota ? (
              <span
                data-testid={`recall-ota-pill-${r.campaign}`}
                className="inline-flex items-center gap-1 rounded-full px-1.5 py-[1px]"
                style={{ background: "rgba(57,255,20,0.1)", border: "1px solid rgba(57,255,20,0.3)", color: "#39FF14" }}
              >
                <ShieldCheck className="w-2.5 h-2.5" /> OTA · Completed
              </span>
            ) : (
              <span
                className="inline-flex items-center rounded-full px-1.5 py-[1px]"
                style={{ border: "1px solid rgba(255,255,255,0.14)", color: "#a1a1aa" }}
              >
                Service visit
              </span>
            )}
            {(r.park_it || r.park_outside) && (
              <span className="inline-flex items-center gap-1" style={{ color: "#FF6B57" }}>
                <AlertTriangle className="w-2.5 h-2.5" /> urgent
              </span>
            )}
          </div>
        </div>
        {open ? (
          <ChevronUp className="w-3.5 h-3.5 text-zinc-500 shrink-0" />
        ) : (
          <ChevronDown className="w-3.5 h-3.5 text-zinc-500 shrink-0" />
        )}
      </div>
      {open && (
        <div className="mt-2 space-y-2 text-[11px] leading-relaxed text-zinc-400">
          <p>{r.summary}</p>
          {r.remedy && (
            <p>
              <span className="font-mono text-[9px] uppercase tracking-[0.14em]" style={{ color: "#39FF14" }}>
                Remedy ·{" "}
              </span>
              {r.remedy}
            </p>
          )}
        </div>
      )}
    </button>
  );
};

/** Live NHTSA recall list for the selected vehicle's model + year. */
export const RecallCheckCard = ({ vehicle }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [open, setOpen] = useState(false);
  const model = baseModel(vehicle);
  const year = vehicle?.year;
  const make = vehicle?.brand === "ford" ? "Ford" : vehicle?.brand === "volvo" ? "Volvo" : "Tesla";

  useEffect(() => {
    if (!model || !year || !API_BASE) return undefined;
    let cancelled = false;
    setData(null);
    setError(false);
    fetch(`${API_BASE}/api/recalls?model=${encodeURIComponent(model)}&year=${year}&make=${encodeURIComponent(make)}`)
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((j) => !cancelled && setData(j))
      .catch(() => !cancelled && setError(true));
    return () => {
      cancelled = true;
    };
  }, [model, year, make]);

  const count = data?.count ?? null;
  const none = count === 0;
  const completed = data?.recalls?.filter((r) => r.ota).length ?? 0;
  const remaining = (count || 0) - completed;
  const shown = expanded ? data?.recalls : data?.recalls?.slice(0, 3);

  return (
    <div
      data-testid="care-recall-check"
      className="rounded-2xl p-4"
      style={{
        background: "rgba(255,255,255,0.02)",
        border: `1px solid ${none ? "rgba(57,255,20,0.25)" : "rgba(255,176,32,0.28)"}`,
      }}
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid="recall-card-toggle"
        aria-expanded={open}
        className="w-full flex items-center gap-2.5 text-left"
      >
        {none ? (
          <ShieldCheck className="w-4 h-4 shrink-0" style={{ color: "#39FF14" }} />
        ) : (
          <ShieldAlert className="w-4 h-4 shrink-0" style={{ color: "#FFB020" }} />
        )}
        <div className="flex-1 min-w-0">
          <div className="text-[12px] font-semibold text-white leading-tight">Recall check</div>
          <div className="font-mono text-[9px] uppercase tracking-[0.14em] text-zinc-500 mt-0.5">
            {year} {model} · NHTSA
          </div>
        </div>
        <div className="shrink-0 flex flex-col items-end gap-1">
          <div
            data-testid="recall-count"
            className="font-mono text-sm font-bold"
            style={{ color: none ? "#39FF14" : "#FFB020" }}
          >
            {error ? "—" : count === null ? "…" : none ? "0 open" : `${count} recall${count === 1 ? "" : "s"}`}
          </div>
          {count > 0 && (
            <div
              data-testid="recall-completed-pill"
              className="inline-flex items-center gap-1 rounded-full px-2 py-[2px] font-mono text-[9px] uppercase tracking-[0.1em]"
              style={{ background: "rgba(57,255,20,0.08)", border: "1px solid rgba(57,255,20,0.25)" }}
            >
              <span style={{ color: "#39FF14" }}>{completed} completed</span>
              <span className="text-zinc-600">·</span>
              <span style={{ color: remaining > 0 ? "#FFB020" : "#71717a" }}>{remaining} remaining</span>
            </div>
          )}
        </div>
        {open ? (
          <ChevronUp className="w-4 h-4 shrink-0 text-zinc-500" />
        ) : (
          <ChevronDown className="w-4 h-4 shrink-0 text-zinc-500" />
        )}
      </button>

      {open && (
        <>
      {error && (
        <p className="text-[11px] text-zinc-500 mt-1">Couldn't reach NHTSA — pull to refresh or try again later.</p>
      )}
      {none && (
        <p className="text-[11px] text-zinc-500 mt-1">No recall campaigns on file for this model year.</p>
      )}

      {shown?.length > 0 && (
        <div className="mt-2 space-y-1.5" data-testid="recall-list">
          {shown.map((r) => (
            <RecallItem key={r.campaign} r={r} />
          ))}
          {data.recalls.length > 3 && (
            <button
              type="button"
              onClick={() => setExpanded((e) => !e)}
              data-testid="recall-expand"
              className="w-full text-center font-mono text-[10px] uppercase tracking-[0.16em] py-1.5 rounded-lg hover:bg-white/[0.04] transition-colors"
              style={{ color: "#00E5FF" }}
            >
              {expanded ? "Show fewer" : `Show all ${data.recalls.length}`}
            </button>
          )}
        </div>
      )}

      {count !== null && !error && (
        <p className="mt-2.5 font-mono text-[9px] leading-relaxed text-zinc-600">
          NHTSA lists campaigns by model year — many recalls are already fixed via OTA update.
          Verify your VIN's open items in the {vehicle?.brand === "ford" ? "FordPass app" : "Tesla app"} › Service.
        </p>
      )}

      {count !== null && !error && count > 0 && (
        <SellerReportRow model={model} year={year} make={make} />
      )}

      {count !== null && !error && (
        <RecallAlertOptIn model={model} year={year} />
      )}
        </>
      )}
    </div>
  );
};

/** Premium seller document — recall compliance PDF (yearly & lifetime). */
const SellerReportRow = ({ model, year, make = "Tesla" }) => {
  useSubscription(); // re-render on tier changes
  const unlocked = hasSellerTools();
  return (
    <div
      data-testid="recall-report-row"
      className="mt-2.5 rounded-xl px-3 py-2.5 flex items-center gap-3"
      style={{ background: "rgba(0,229,255,0.04)", border: "1px solid rgba(0,229,255,0.18)" }}
    >
      <div className="flex-1 min-w-0">
        <div className="text-[11px] font-semibold text-white leading-tight">Selling this car?</div>
        <div className="text-[10px] text-zinc-500 leading-snug mt-0.5">
          Download the buyer-ready recall compliance report — every campaign with its completion status.
        </div>
      </div>
      {unlocked ? (
        <a
          href={`${API_BASE}/api/recalls/report.pdf?model=${encodeURIComponent(model)}&year=${year}&make=${encodeURIComponent(make)}`}
          data-testid="recall-report-download"
          className="shrink-0 inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 font-mono text-[9px] uppercase tracking-[0.12em] text-black font-bold"
          style={{ background: "linear-gradient(90deg,#39FF14,#00E5FF)" }}
        >
          <FileDown className="w-3 h-3" /> PDF
        </a>
      ) : (
        <button
          type="button"
          onClick={() => openSubscribeGate("recall-report")}
          data-testid="recall-report-locked"
          className="shrink-0 inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 font-mono text-[9px] uppercase tracking-[0.12em]"
          style={{ border: "1px solid rgba(0,229,255,0.4)", color: "#00E5FF" }}
        >
          <Lock className="w-3 h-3" /> Premium
        </button>
      )}
    </div>
  );
};

/** Email opt-in — "tell me the moment NHTSA opens a new recall for my car". */
const RecallAlertOptIn = ({ model, year }) => {
  const storageKey = `eviq.recall_alert.${model}.${year}`.toLowerCase();
  const [email, setEmail] = useState(() => window.localStorage.getItem("eviq.recall_alert_email") || "");
  const [state, setState] = useState(() => (window.localStorage.getItem(storageKey) ? "done" : "idle"));

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setState("busy");
    try {
      const r = await fetch(`${API_BASE}/api/recalls/subscribe`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim(), model, year }),
      });
      if (!r.ok) throw new Error();
      window.localStorage.setItem("eviq.recall_alert_email", email.trim());
      window.localStorage.setItem(storageKey, "1");
      setState("done");
    } catch {
      setState("error");
    }
  };

  if (state === "done") {
    return (
      <div
        data-testid="recall-alert-subscribed"
        className="mt-2.5 rounded-xl px-3 py-2 font-mono text-[10px] uppercase tracking-[0.12em]"
        style={{ background: "rgba(57,255,20,0.07)", border: "1px solid rgba(57,255,20,0.25)", color: "#39FF14" }}
      >
        ✓ Alerts on — we'll email you when NHTSA opens a new {year} {model} recall
      </div>
    );
  }
  return (
    <form onSubmit={submit} className="mt-2.5 flex items-center gap-2" data-testid="recall-alert-form">
      <input
        type="email"
        required
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="you@email.com"
        data-testid="recall-alert-email"
        className="flex-1 min-w-0 rounded-full bg-white/[0.04] px-3 py-1.5 text-[11px] text-white placeholder:text-zinc-600 outline-none focus:ring-1 focus:ring-[#00E5FF]/50"
        style={{ border: "1px solid rgba(255,255,255,0.12)" }}
      />
      <button
        type="submit"
        disabled={state === "busy"}
        data-testid="recall-alert-submit"
        className="shrink-0 rounded-full px-3 py-1.5 font-mono text-[9px] uppercase tracking-[0.12em] text-black font-bold disabled:opacity-60"
        style={{ background: "linear-gradient(90deg,#39FF14,#00E5FF)" }}
      >
        {state === "busy" ? "…" : "Alert me"}
      </button>
      {state === "error" && (
        <span className="font-mono text-[9px] text-[#FF6B57]">Failed — try again</span>
      )}
    </form>
  );
};
